/**
 * Service worker registration — push/PWA only.
 * CSS/JS are never cached by the SW (see public/sw.js).
 */

const SHELL_CACHE_CURRENT = 'spenderly-shell-v7';

/** Wipe every Cache Storage entry (old poisoned CSS/JS shells). */
export async function clearAllShellCaches() {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((k) => caches.delete(k)));
}

/**
 * One-time + broken-CSS recovery. Also used if an old SW is still controlling.
 */
export async function recoverPoisonedShellOnce() {
  if (typeof window === 'undefined') return;

  try {
    await clearAllShellCaches();
  } catch { /* ignore */ }

  const cssMissing = () => {
    try {
      return !getComputedStyle(document.documentElement).getPropertyValue('--bg-primary').trim();
    } catch {
      return false;
    }
  };

  const nukeAndReload = async () => {
    if (sessionStorage.getItem('spenderly_css_recover_reloaded') === '1') return;
    sessionStorage.setItem('spenderly_css_recover_reloaded', '1');
    try {
      await clearAllShellCaches();
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* ignore */ }
    window.location.reload();
  };

  const check = () => {
    if (cssMissing()) nukeAndReload();
  };

  if (document.readyState === 'complete') setTimeout(check, 80);
  else window.addEventListener('load', () => setTimeout(check, 80));

  // New SW activated → hard reload once so tabs drop old controllers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SPENDERLY_SW_ACTIVATED') {
        if (sessionStorage.getItem('spenderly_sw_activated_reload') === '1') return;
        sessionStorage.setItem('spenderly_sw_activated_reload', '1');
        window.location.reload();
      }
    });
  }
}

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  const isLocalhost = Boolean(
    window.location.hostname === 'localhost'
      || window.location.hostname === '[::1]'
      || window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/)
  );

  const enable = process.env.NODE_ENV === 'production' || isLocalhost;
  if (!enable) return;

  const notifyUpdate = () => {
    window.dispatchEvent(new CustomEvent('spenderly:sw-update'));
  };

  window.addEventListener('load', () => {
    // Wait until CSS applied once on this origin before registering SW
    setTimeout(() => {
      if (typeof window.__SP_ALLOW_SW === 'function' && !window.__SP_ALLOW_SW()) {
        return;
      }

      const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js?v=7`;
      navigator.serviceWorker
        .register(swUrl)
        .then(async (reg) => {
          // Force update check every load
          reg.update().catch(() => {});

          if (reg.waiting && navigator.serviceWorker.controller) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            notifyUpdate();
          }

          reg.onupdatefound = () => {
            const installing = reg.installing;
            if (!installing) return;
            installing.onstatechange = () => {
              if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                installing.postMessage({ type: 'SKIP_WAITING' });
                notifyUpdate();
              }
            };
          };

          const check = () => {
            if (document.visibilityState === 'visible') {
              reg.update().catch(() => {});
            }
          };
          document.addEventListener('visibilitychange', check);
          setInterval(check, 60 * 60 * 1000);
        })
        .catch((err) => {
          console.warn('[spenderly] Service worker registration failed:', err);
        });
    }, 500);

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      if (sessionStorage.getItem('spenderly_controller_reloaded') === '1') return;
      refreshing = true;
      sessionStorage.setItem('spenderly_controller_reloaded', '1');
      window.location.reload();
    });
  });
}

/** Apply waiting worker and reload so the new shell loads. */
export async function applyServiceWorkerUpdate() {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg?.waiting) {
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
  window.location.reload();
}

export function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  clearAllShellCaches().catch(() => {});
  navigator.serviceWorker.getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => {});
}
